// src/contexts/EmailContext.tsx

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { ImapEmail as FetchedEmail } from "../types/imapEmail";
import { useEmailService } from "../services/api/emailService"; // new custom hook
import { useAuth } from "./AuthContext";

/**
 * Utility for saving fetched email data to local storage (for dev).
 * You can remove or adjust if not needed.
 */
const saveEmailsToFile = (emails: FetchedEmail[]) => {
  if (process.env.NODE_ENV !== "development") return;
  try {
    localStorage.setItem("fetchedEmails", JSON.stringify(emails));
  } catch (err) {
    // Silently fail in production
  }
};

export interface EmailThreadGroup {
  threads: FetchedEmail[][]; // Array of threads (each thread is an array of emails)
  threadCount: number;
}

export interface EmailHierarchy {
  [accountType: string]: {
    [folder: string]: EmailThreadGroup;
  };
}

interface EmailContextType {
  emails: FetchedEmail[];
  threads: FetchedEmail[][];
  emailHierarchy: EmailHierarchy;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  refresh: (force?: boolean) => Promise<{ emails: FetchedEmail[]; threads: FetchedEmail[][] }>;
  getThread: (threadId: string) => Promise<FetchedEmail[] | null>;
  getEmail: (emailId: string) => FetchedEmail | undefined;
  /**
   * A method for sending or replying to an email, 
   * using the new 'sendEmail' from the custom hook 
   * that accepts a payload with fromEmail, recipient, attachments, etc.
   */
  sendReply: (replyData: any) => Promise<any>;
  lastRefresh: Date | null;
  account: "all";
}

const EmailContext = createContext<EmailContextType | undefined>(undefined);

export const useEmailContext = (): EmailContextType => {
  const context = useContext(EmailContext);
  if (!context) {
    throw new Error("useEmailContext must be used within an EmailProvider");
  }
  return context;
};

export const EmailProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

  // Pull methods from your custom email hook
  const { fetchImapEmails, sendEmail } = useEmailService();

  // State for all emails and structured data
  const [emails, setEmails] = useState<FetchedEmail[]>([]);
  const [threads, setThreads] = useState<FetchedEmail[][]>([]);
  const [emailHierarchy, setEmailHierarchy] = useState<EmailHierarchy>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // If you need Auth data for some reason
  const { user, isAuthenticated } = useAuth() as any;
  // 'as any' is optional. Adjust or remove if your AuthContext type is set up.

  // Refs for throttling
  const currentFetchRef = useRef<number | null>(null);
  const pendingFetchPromiseRef = useRef<Promise<{ emails: FetchedEmail[]; threads: FetchedEmail[][] }> | null>(null);
  const lastFetchStartedRef = useRef<number | null>(null);

  const shouldFetch = useCallback((): boolean => {
    if (!lastRefresh) return true;
    return new Date().getTime() - lastRefresh.getTime() > 2 * 60 * 1000; // e.g. 2 minute threshold
  }, [lastRefresh]);

  const normalizeId = useCallback((id: string): string => {
    return id.replace(/^<|>$/g, "").trim();
  }, []);

  /**
   * Helper to build "threads" from a flat array of FetchedEmail.
   * This logic attempts to connect each email with its root parent if inReplyTo is present.
   */
  const buildThreads = useCallback((allEmails: FetchedEmail[]): FetchedEmail[][] => {
    const threadMap: { [rootId: string]: FetchedEmail[] } = {};

    const findRootEmail = (email: FetchedEmail): FetchedEmail => {
      let current = email;
      const visited = new Set<string>();
      while (current.inReplyTo) {
        const normalizedParentId = normalizeId(current.inReplyTo);
        if (visited.has(normalizedParentId)) break;
        visited.add(normalizedParentId);
        const parent = allEmails.find((e) => normalizeId(e.id) === normalizedParentId);
        if (!parent) {
          // Check references if no direct parent found
          if (current.references && current.references.length > 0) {
            const normalizedRefId = normalizeId(current.references[0]);
            if (!visited.has(normalizedRefId)) {
              const refParent = allEmails.find((e) => normalizeId(e.id) === normalizedRefId);
              if (refParent) {
                current = refParent;
                continue;
              }
            }
          }
          break;
        }
        current = parent;
      }
      return current;
    };

    allEmails.forEach((email) => {
      const rootEmail = findRootEmail(email);
      const rootKey = normalizeId(rootEmail.id);
      if (!threadMap[rootKey]) {
        threadMap[rootKey] = [];
      }
      threadMap[rootKey].push(email);
    });

    // Sort each thread by ascending date
    Object.keys(threadMap).forEach((key) => {
      threadMap[key].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    });

    return Object.values(threadMap);
  }, [normalizeId]);

  /**
   * Build an EmailHierarchy object, grouping emails by accountType -> folder -> threads
   */
  const buildEmailHierarchy = useCallback((allEmails: FetchedEmail[]): EmailHierarchy => {
    const hierarchy: EmailHierarchy = {};

    allEmails.forEach((email) => {
      const accType = email.accountType;
      const folder = email.folder;
      if (!hierarchy[accType]) {
        hierarchy[accType] = {};
      }
      if (!hierarchy[accType][folder]) {
        hierarchy[accType][folder] = { threads: [], threadCount: 0 };
      }
    });

    for (const accType in hierarchy) {
      for (const folder in hierarchy[accType]) {
        const groupEmails = allEmails.filter(
          (e) => e.accountType === accType && e.folder === folder
        );
        const threadsForFolder = buildThreads(groupEmails);
        hierarchy[accType][folder].threads = threadsForFolder;
        hierarchy[accType][folder].threadCount = threadsForFolder.length;
      }
    }
    return hierarchy;
  }, [buildThreads]);

  /**
   * Throttle & fetch the user's emails, building threads/hierarchy
   */
  const refresh = useCallback(
    async (force = false): Promise<{ emails: FetchedEmail[]; threads: FetchedEmail[][] }> => {
      const now = Date.now();
      
      // Early exit if another fetch is already in progress
      if (refreshing && !force) {
        pendingFetchPromiseRef.current = pendingFetchPromiseRef.current || Promise.resolve({ emails, threads });
        return pendingFetchPromiseRef.current;
      }
      
      // Implement a minimum time between fetches to prevent excessive API calls
      const minFetchInterval = 3000; // e.g. 3 seconds
      if (!force && lastFetchStartedRef.current && now - lastFetchStartedRef.current < minFetchInterval) {
        pendingFetchPromiseRef.current = pendingFetchPromiseRef.current || Promise.resolve({ emails, threads });
        return pendingFetchPromiseRef.current;
      }
      
      if (pendingFetchPromiseRef.current) {
        return pendingFetchPromiseRef.current;
      }
      
      const requestId = Date.now();
      currentFetchRef.current = requestId;
      lastFetchStartedRef.current = now;
      setRefreshing(true);
      setError(null);
      
      if (!isAuthenticated) {
        setRefreshing(false);
        return { emails, threads };
      }
      
      const fetchPromise = (async () => {
        try {
          // Pass true to fetchImapEmails to trigger the email fetch process
          const fetchedEmails = await fetchImapEmails(true);
          saveEmailsToFile(fetchedEmails);

          const processedEmails = fetchedEmails.map((email) => ({
            ...email,
            folder: email.folder,
          }));

          const builtThreads = buildThreads(processedEmails);

          const hierarchy = buildEmailHierarchy(processedEmails);

          if (currentFetchRef.current === requestId) {
            setEmails(processedEmails);
            setThreads(builtThreads);
            setEmailHierarchy(hierarchy);
            setLastRefresh(new Date());
            setError(null);
            setLoading(false);
            return { emails: processedEmails, threads: builtThreads };
          }
          return { emails, threads };
        } catch (err: any) {
          if (currentFetchRef.current === requestId) {
            setError("Failed to fetch emails. Please try again later.");
            setLoading(false);
          }
          throw err;
        } finally {
          if (currentFetchRef.current === requestId) {
            setRefreshing(false);
            pendingFetchPromiseRef.current = null;
          }
        }
      })();

      pendingFetchPromiseRef.current = fetchPromise;
      return fetchPromise;
    },
    [emails, threads, buildThreads, buildEmailHierarchy, isAuthenticated]
  );

  /**
   * Retrieve a specific thread by ID, optionally refetching data if needed.
   */
  const getThread = useCallback(
    async (threadId: string): Promise<FetchedEmail[] | null> => {
      const foundThread = threads.find((t) => t[0].id === threadId);
      if (foundThread) return foundThread;

      if (shouldFetch()) {
        await refresh(true);
        const refreshedThread = threads.find((t) => t[0].id === threadId);
        return refreshedThread || null;
      }
      return null;
    },
    [threads, refresh, shouldFetch]
  );

  /**
   * Retrieve a single email by ID from our in-memory cache.
   */
  const getEmail = useCallback(
    (emailId: string): FetchedEmail | undefined => {
      return emails.find((e) => e.id === emailId);
    },
    [emails]
  );

  /**
   * Called when a user wants to send or reply to an email.
   * This method delegates to our new 'sendEmail' function from the custom hook.
   */
  const sendReply = async (replyData: any): Promise<any> => {
    try {
      // 'sendEmail' handles the actual API call to /emails/send
      const result = await sendEmail(replyData);
      return result;
    } catch (error) {
      throw error;
    }
  };

  // On mount or tokens update, load data if needed
  useEffect(() => {
    const loadData = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      if (!shouldFetch()) {
        setLoading(false);
        return;
      }
      try {
        await refresh(true);
      } catch (err) {
        setError("Failed to load emails. Please refresh to try again.");
        setLoading(false);
      }
    };
    loadData();
  }, [refresh, shouldFetch, isAuthenticated]);

  // Periodically auto-refresh
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!document.hidden && !refreshing && !error && shouldFetch() && isAuthenticated) {
        refresh(false).catch(() => {});
      }
    }, 60000);

    return () => clearInterval(intervalId);
  }, [refresh, refreshing, error, shouldFetch, isAuthenticated]);

  /**
   * The context value that we provide to children
   */
  const contextValue = useMemo(
    () => ({
      emails,
      threads,
      emailHierarchy,
      loading,
      refreshing,
      error,
      refresh,
      getThread,
      getEmail,
      sendReply,
      lastRefresh,
      account: "all" as const,
    }),
    [
      emails,
      threads,
      emailHierarchy,
      loading,
      refreshing,
      error,
      refresh,
      getThread,
      getEmail,
      sendReply,
      lastRefresh,
    ]
  );

  return (
    <EmailContext.Provider value={contextValue}>
      {children}
    </EmailContext.Provider>
  );
};

export default EmailContext;
